import type { IconType } from 'react-icons';
import {
  LuLayoutDashboard,
  LuPackage,
  LuTruck,
  LuUsers,
  LuSettings,
  LuCar,
  LuUserCheck,
  LuWrench,
  LuMap,
  LuMapPin,
  LuDollarSign,
  LuBell,
  LuShield,
  LuBuilding2,
  LuPuzzle,
  LuDatabase,
  LuFileText,
  LuChartLine,
  LuCalendar,
  LuBadgeDollarSign,
  LuUpload,
  LuHistory,
  LuFileSpreadsheet,
  LuGitMerge,
  LuListTree,
  LuShieldCheck,
  LuWand,
  LuBook,
  LuRoute,
  LuBox,
  LuBuilding,
  LuReceipt,
  LuUser,
  LuIdCard,
  LuCog,
  LuContainer,
  LuWarehouse,
  LuLink,
  LuNetwork,
  LuGitBranch,
  LuArrowLeftRight,
  LuClipboardList,
  LuSend,
  LuGrid3X3,
  LuLandmark,
  LuScrollText,
  LuScanSearch,
  LuCreditCard,
  LuWallet,
  LuShieldAlert,
  LuCalendarClock,
  LuFlag,
  LuList,
  LuZap,
  LuClipboard,
  LuCircleCheck,
  LuSearch,
  LuFolderOpen,
  LuLayers,
  LuScale,
  LuCalculator,
  LuFuel,
  LuTimer,
  LuClipboardCheck,
  LuBanknote,
  LuGlobe,
  LuBot,
  LuHandCoins,
} from 'react-icons/lu';

export interface NavigationItem {
  id: string;
  label: string;
  labelKey?: string; // i18n translation key
  href?: string;
  icon?: IconType;
  children?: NavigationItem[];
  badge?: {
    content: string | number;
    colorPalette?: string;
  };
  divider?: boolean;
  sectionLabel?: boolean; // Section header (e.g., "OPERATIONS", "FLEET")
  permission?: string;
  moduleId?: string; // Maps to ModuleId enum for Core/Available tier assignment per persona
  /**
   * Restrict this item to specific country codes (e.g. ['US']). When set, the
   * item is hidden unless the current user/tenant country is in the list.
   * Keeps US-only modules (e.g. IFTA reporting) out of TR tenants (B-181).
   * Undefined → visible everywhere.
   */
  countries?: string[];
}

export const mvpNavigationItems: NavigationItem[] = [
  // ── OPERATIONS ──────────────────────────────────────────
  {
    id: 'section-operations',
    label: 'Operations',
    labelKey: 'navigation.sections.operations',
    sectionLabel: true,
  },
  {
    id: 'dashboard',
    moduleId: 'dashboard',
    label: 'Dashboard',
    labelKey: 'navigation.dashboard',
    href: '/dashboard',
    icon: LuLayoutDashboard,
  },
  {
    id: 'orders',
    moduleId: 'orders',
    label: 'Orders',
    labelKey: 'navigation.orders',
    href: '/orders',
    icon: LuPackage,
    children: [
      {
        id: 'orders-all',
        label: 'All Orders',
        labelKey: 'navigation.ordersAll',
        href: '/orders',
        icon: LuPackage,
      },
      {
        id: 'load-requests',
        label: 'Load Requests',
        labelKey: 'navigation.ordersLoadRequests',
        href: '/orders/load-requests',
        icon: LuClipboardList,
        moduleId: 'load-requests',
      },
    ],
  },
  {
    id: 'shipments',
    moduleId: 'shipments',
    label: 'Shipments',
    labelKey: 'navigation.shipments.title',
    icon: LuTruck,
    children: [
      {
        id: 'shipments-all',
        label: 'All Shipments',
        labelKey: 'navigation.shipments.all',
        href: '/shipments',
        icon: LuList,
      },
      {
        id: 'shipments-active',
        label: 'Active',
        labelKey: 'navigation.shipments.active',
        href: '/shipments/active',
        icon: LuZap,
      },
      {
        id: 'shipments-planning',
        label: 'Planning',
        labelKey: 'navigation.shipments.planning',
        href: '/shipments/planning',
        icon: LuClipboard,
      },
      {
        id: 'shipments-completed',
        label: 'Completed',
        labelKey: 'navigation.shipments.completed',
        href: '/shipments/completed',
        icon: LuCircleCheck,
      },
    ],
  },
  {
    id: 'dispatch',
    moduleId: 'dispatch',
    label: 'AI Dispatch',
    labelKey: 'navigation.dispatch.title',
    href: '/dispatch',
    icon: LuSend,
  },
  {
    // Phase 1 deferred polish — `AgentRunViewer` route shipped Apr 19
    // (PR #171) but stayed unreachable from the sidebar. Surface here
    // alongside AI Dispatch so operators reach it without typing the
    // URL. Permission-gated to operator+ via existing role guard.
    id: 'agents',
    moduleId: 'dispatch',
    label: 'AI Agents',
    labelKey: 'navigation.agents',
    href: '/agents',
    icon: LuBot,
  },
  {
    id: 'trips',
    moduleId: 'trips',
    label: 'Trips',
    labelKey: 'navigation.trips.title',
    href: '/trips',
    icon: LuRoute,
  },
  {
    id: 'tracking',
    moduleId: 'tracking',
    label: 'Live Tracking',
    labelKey: 'navigation.tracking',
    href: '/tracking',
    icon: LuMapPin,
    badge: {
      content: 0,
      colorPalette: 'green',
    },
  },

  // ── LOGISTICS ───────────────────────────────────────────
  {
    id: 'section-logistics',
    label: 'Logistics',
    labelKey: 'navigation.sections.logistics',
    sectionLabel: true,
  },
  {
    id: 'routes',
    moduleId: 'routes',
    label: 'Routes',
    labelKey: 'navigation.routes',
    href: '/routes',
    icon: LuMap,
  },
  {
    id: 'carriers',
    moduleId: 'carriers',
    label: 'Carriers',
    labelKey: 'navigation.carriers.title',
    href: '/carriers',
    icon: LuBuilding,
  },
  {
    id: 'rates',
    moduleId: 'rates',
    label: 'Carrier Rates',
    labelKey: 'navigation.rates.title',
    icon: LuBadgeDollarSign,
    children: [
      {
        id: 'rate-search',
        label: 'Search Rates',
        labelKey: 'navigation.rates.search',
        href: '/rates',
        icon: LuSearch,
      },
      {
        id: 'quote-history',
        label: 'Quote History',
        labelKey: 'navigation.rates.quotes',
        href: '/rates/quotes',
        icon: LuHistory,
      },
      {
        id: 'contract-rates',
        label: 'Carrier Contracts',
        labelKey: 'navigation.rates.contracts',
        href: '/contract-rates',
        icon: LuFileText,
      },
      {
        id: 'fulfillment',
        label: 'Fulfillment Analysis',
        labelKey: 'navigation.rates.fulfillment',
        href: '/fulfillment',
        icon: LuScale,
      },
      {
        id: 'load-postings',
        label: 'Load Board',
        labelKey: 'navigation.rates.loadBoard',
        href: '/load-postings',
        icon: LuSend,
      },
      {
        id: 'rates-analytics',
        label: 'Analytics',
        labelKey: 'navigation.rates.analytics',
        href: '/rates/analytics',
        icon: LuChartLine,
      },
    ],
  },
  {
    id: 'customers',
    moduleId: 'customers',
    label: 'Customers',
    labelKey: 'navigation.customers',
    href: '/customers',
    icon: LuUsers,
  },
  {
    id: 'zones',
    moduleId: 'zones',
    label: 'Zones',
    labelKey: 'navigation.zones.title',
    href: '/zones',
    icon: LuGrid3X3,
  },
  {
    id: 'consolidation',
    moduleId: 'consolidation',
    label: 'Consolidation',
    labelKey: 'navigation.consolidation.title',
    icon: LuBox,
    children: [
      {
        id: 'consolidation-opportunities',
        label: 'Opportunities',
        labelKey: 'navigation.consolidation.opportunities',
        href: '/consolidation/opportunities',
        icon: LuFolderOpen,
      },
      {
        id: 'consolidation-groups',
        label: 'Shipment Groups',
        labelKey: 'navigation.consolidation.groups',
        href: '/consolidation/groups',
        icon: LuLayers,
      },
    ],
  },
  {
    id: 'logistics',
    moduleId: 'logistics',
    label: 'Multi-Leg',
    labelKey: 'navigation.logistics.title',
    icon: LuNetwork,
    children: [
      {
        id: 'distribution-hubs',
        label: 'Distribution Hubs',
        labelKey: 'navigation.logistics.distributionHubs',
        href: '/logistics/distribution-hubs',
        icon: LuLandmark,
      },
      {
        id: 'shipment-legs',
        label: 'Shipment Legs',
        labelKey: 'navigation.logistics.shipmentLegs',
        href: '/logistics/shipment-legs',
        icon: LuGitBranch,
      },
      {
        id: 'cross-docking',
        label: 'Cross-Docking',
        labelKey: 'navigation.logistics.crossDocking',
        href: '/logistics/cross-docking',
        icon: LuArrowLeftRight,
      },
    ],
  },

  // ── FLEET ───────────────────────────────────────────────
  {
    id: 'section-fleet',
    label: 'Fleet',
    labelKey: 'navigation.sections.fleet',
    sectionLabel: true,
  },
  {
    id: 'fleet',
    moduleId: 'fleet',
    label: 'Equipment',
    labelKey: 'navigation.fleet.title',
    icon: LuCar,
    children: [
      {
        id: 'power-units',
        label: 'Power Units',
        labelKey: 'navigation.fleet.powerUnits',
        href: '/fleet/power-units',
        icon: LuCog,
      },
      {
        id: 'trailers',
        label: 'Trailers',
        labelKey: 'navigation.fleet.trailers',
        href: '/fleet/trailers',
        icon: LuContainer,
      },
      {
        id: 'pool',
        label: 'Pool Management',
        labelKey: 'navigation.fleet.pool',
        href: '/fleet/pool',
        icon: LuWarehouse,
      },
      {
        id: 'assignments',
        label: 'Assignments',
        labelKey: 'navigation.fleet.assignments',
        href: '/fleet/assignments',
        icon: LuLink,
      },
    ],
  },
  {
    id: 'drivers',
    moduleId: 'drivers',
    label: 'Drivers',
    labelKey: 'navigation.fleet.drivers',
    href: '/fleet/drivers',
    icon: LuUserCheck,
  },
  {
    id: 'maintenance',
    moduleId: 'maintenance',
    label: 'Maintenance',
    labelKey: 'navigation.fleet.maintenance',
    href: '/fleet/maintenance',
    icon: LuWrench,
  },
  {
    id: 'fuel-logs',
    moduleId: 'fuel-logs',
    label: 'Fuel Tracking',
    labelKey: 'navigation.fleet.fuelLogs',
    href: '/fleet/fuel-logs',
    icon: LuFuel,
  },
  {
    id: 'inspections',
    moduleId: 'inspections',
    label: 'Inspections',
    labelKey: 'navigation.fleet.inspections',
    href: '/fleet/inspections',
    icon: LuClipboardCheck,
  },
  {
    id: 'hos',
    moduleId: 'hos',
    label: 'HOS Tracking',
    labelKey: 'navigation.fleet.hos',
    href: '/fleet/drivers/hos',
    icon: LuTimer,
    // HOS (Hours of Service) is the US FMCSA / ELD driving-time regime — TR uses
    // its own Takograf/AETR rules, so hide this US-specific screen for non-US
    // tenants (same gating as IFTA, B-181).
    countries: ['US'],
  },
  {
    id: 'carrier-safety',
    moduleId: 'carrier-safety',
    label: 'Carrier Safety',
    labelKey: 'navigation.fleet.carrierSafety',
    href: '/compliance/carrier-safety',
    icon: LuShieldCheck,
    // Carrier Safety (FMCSA CSA / SMS scores) is US-only — hide for non-US
    // tenants (B-181).
    countries: ['US'],
  },
  {
    id: 'ifta',
    label: 'IFTA Reporting',
    labelKey: 'navigation.fleet.ifta',
    href: '/compliance/ifta',
    icon: LuCalculator,
    // B-181: IFTA (International Fuel Tax Agreement) is a US/Canada concept —
    // hide it for non-US tenants (e.g. TR).
    countries: ['US'],
  },

  // ── FINANCE ─────────────────────────────────────────────
  {
    id: 'section-finance',
    label: 'Finance',
    labelKey: 'navigation.sections.finance',
    sectionLabel: true,
  },
  {
    id: 'finance',
    label: 'Finance',
    labelKey: 'navigation.finance.title',
    icon: LuDollarSign,
    children: [
      {
        id: 'invoices',
        moduleId: 'invoices',
        label: 'Invoices',
        labelKey: 'navigation.finance.invoices',
        href: '/finance/invoices',
        icon: LuReceipt,
      },
      {
        id: 'payments',
        moduleId: 'payments',
        label: 'Payments',
        labelKey: 'navigation.finance.payments',
        href: '/finance/payments',
        icon: LuCreditCard,
      },
      {
        id: 'expenses',
        moduleId: 'expenses',
        label: 'Expenses',
        labelKey: 'navigation.finance.expenses',
        href: '/finance/expenses',
        icon: LuWallet,
      },
      {
        id: 'contracts',
        label: 'Customer Agreements',
        labelKey: 'navigation.finance.contracts',
        href: '/finance/contracts',
        icon: LuScrollText,
      },
      {
        id: 'claims',
        label: 'Claims',
        labelKey: 'navigation.finance.claims',
        href: '/finance/claims',
        icon: LuShieldAlert,
      },
      {
        id: 'capacity-reservations',
        label: 'Capacity Reservations',
        labelKey: 'navigation.finance.capacityReservations',
        href: '/finance/capacity-reservations',
        icon: LuCalendarClock,
      },
      {
        id: 'milestones',
        label: 'Milestones',
        labelKey: 'navigation.finance.milestones',
        href: '/finance/milestones',
        icon: LuFlag,
      },
      {
        id: 'factoring',
        moduleId: 'factoring',
        label: 'Factoring',
        labelKey: 'navigation.finance.factoring',
        href: '/finance/factoring',
        icon: LuBanknote,
      },
      {
        id: 'driver-settlements',
        moduleId: 'driver-settlements',
        label: 'Driver Settlements',
        labelKey: 'navigation.finance.driverSettlements',
        href: '/finance/driver-settlements',
        icon: LuWallet,
      },
    ],
  },

  // ── DOCUMENTS & REPORTS ─────────────────────────────────
  {
    id: 'section-docs',
    label: 'Insights',
    labelKey: 'navigation.sections.insights',
    sectionLabel: true,
  },
  {
    id: 'documents',
    moduleId: 'documents',
    label: 'Documents',
    labelKey: 'navigation.documents',
    href: '/documents',
    icon: LuFileText,
  },
  {
    id: 'reports',
    moduleId: 'reports',
    label: 'Reports',
    labelKey: 'navigation.reports.title',
    icon: LuChartLine,
    children: [
      {
        id: 'reports-overview',
        label: 'Overview',
        labelKey: 'navigation.reports.overview',
        href: '/reports',
        icon: LuChartLine,
      },
      {
        id: 'reports-schedules',
        label: 'Schedules',
        labelKey: 'navigation.reports.schedules',
        href: '/reports/schedules',
        icon: LuCalendar,
      },
      {
        id: 'reports-designer',
        label: 'Designer',
        labelKey: 'navigation.reports.designer',
        href: '/reports/designer',
        icon: LuWand,
      },
    ],
  },
  {
    id: 'system-flow',
    label: 'System Flow',
    labelKey: 'navigation.systemFlow',
    href: '/system-flow',
    icon: LuGitBranch,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    labelKey: 'navigation.notifications',
    href: '/notifications',
    icon: LuBell,
  },

  // ── SYSTEM ──────────────────────────────────────────────
  {
    id: 'separator-system',
    label: '',
    divider: true,
  },
  {
    id: 'admin',
    label: 'Administration',
    labelKey: 'navigation.admin.title',
    icon: LuShield,
    permission: '*',
    children: [
      {
        id: 'admin-dashboard',
        label: 'Platform Dashboard',
        labelKey: 'navigation.admin.dashboard.title',
        href: '/admin',
        icon: LuChartLine,
      },
      {
        id: 'users',
        label: 'Users',
        labelKey: 'navigation.admin.users.title',
        href: '/admin/users',
        icon: LuUsers,
      },
      {
        id: 'tenants',
        label: 'Tenants',
        labelKey: 'navigation.admin.tenants.title',
        href: '/admin/tenants',
        icon: LuBuilding2,
      },
      {
        id: 'audit-logs',
        label: 'Audit Logs',
        labelKey: 'navigation.admin.auditLogs',
        href: '/admin/audit-logs',
        icon: LuScanSearch,
      },
      {
        id: 'integrations',
        label: 'Integrations',
        labelKey: 'navigation.admin.integrations',
        href: '/admin/integrations',
        icon: LuPuzzle,
      },
      {
        id: 'system',
        label: 'System',
        labelKey: 'navigation.admin.system',
        href: '/admin/system',
        icon: LuDatabase,
      },
      {
        id: 'demo',
        label: 'Demo',
        labelKey: 'navigation.admin.demo',
        href: '/admin/demo',
        icon: LuZap,
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    labelKey: 'navigation.settings.title',
    icon: LuSettings,
    children: [
      {
        id: 'settings-users',
        label: 'User Management',
        labelKey: 'navigation.settings.users',
        href: '/settings/users',
        icon: LuUsers,
        permission: 'admin',
      },
      {
        id: 'settings-subscription',
        label: 'Subscription',
        labelKey: 'navigation.settings.subscription',
        href: '/settings/subscription',
        icon: LuDollarSign,
        permission: 'admin',
      },
      {
        id: 'settings-profile',
        label: 'Profile',
        labelKey: 'navigation.settings.profile',
        href: '/settings/profile',
        icon: LuUser,
      },
      {
        id: 'settings-tenant',
        label: 'Tenant',
        labelKey: 'navigation.settings.tenant',
        href: '/settings/tenant',
        icon: LuIdCard,
      },
      {
        id: 'settings-notifications',
        label: 'Notifications',
        labelKey: 'navigation.settings.notifications',
        href: '/settings/notifications',
        icon: LuBell,
      },
      {
        id: 'settings-customer-portal',
        label: 'Customer Portal',
        labelKey: 'navigation.settings.customerPortal',
        href: '/settings/customer-portal',
        icon: LuGlobe,
      },
      {
        id: 'settings-saved-addresses',
        label: 'Saved Addresses',
        labelKey: 'navigation.settings.savedAddresses',
        href: '/settings/saved-addresses',
        icon: LuMapPin,
      },
      {
        id: 'settings-order-rules',
        label: 'Order Rules',
        labelKey: 'navigation.settings.orderRules',
        href: '/settings/order-rules',
        icon: LuClipboardList,
        permission: 'admin',
      },
      {
        id: 'settings-integrations',
        label: 'Integrations',
        labelKey: 'navigation.settings.integrations',
        href: '/settings/integrations',
        icon: LuPuzzle,
        permission: 'admin',
      },
      {
        id: 'settings-fleet-costs',
        label: 'Fleet Costs',
        labelKey: 'navigation.settings.fleetCosts',
        href: '/settings/fleet-costs',
        icon: LuCalculator,
        permission: 'admin',
      },
      {
        id: 'settings-fulfillment-rules',
        label: 'Fulfillment Rules',
        labelKey: 'navigation.settings.fulfillmentRules',
        href: '/settings/fulfillment-rules',
        icon: LuZap,
        permission: 'admin',
      },
      {
        id: 'settings-vehicle-capacity',
        label: 'Vehicle Capacity',
        labelKey: 'navigation.settings.vehicleCapacity',
        href: '/settings/vehicle-capacity',
        icon: LuContainer,
        permission: 'admin',
      },
      {
        id: 'settings-guided-tours',
        label: 'Guided Tours',
        labelKey: 'navigation.settings.guidedTours',
        href: '/settings/guided-tours',
        icon: LuRoute,
        permission: 'admin',
      },
      {
        id: 'settings-document-templates',
        label: 'Document Templates',
        labelKey: 'navigation.settings.documentTemplates',
        href: '/settings/document-templates',
        icon: LuScrollText,
        permission: 'admin',
      },
      {
        id: 'settings-pay-plans',
        label: 'Pay Plans',
        labelKey: 'navigation.settings.payPlans',
        href: '/settings/pay-plans',
        icon: LuHandCoins,
        permission: 'admin',
      },
      {
        id: 'settings-data-import',
        label: 'Data Import',
        labelKey: 'navigation.settings.dataImport.title',
        icon: LuUpload,
        permission: 'admin',
        children: [
          {
            id: 'import-new',
            label: 'Import Data',
            labelKey: 'navigation.settings.dataImport.importData',
            href: '/settings/data-import',
            icon: LuUpload,
          },
          {
            id: 'import-history',
            label: 'Import History',
            labelKey: 'navigation.settings.dataImport.history',
            href: '/settings/data-import/history',
            icon: LuHistory,
          },
          {
            id: 'import-templates',
            label: 'Templates',
            labelKey: 'navigation.settings.dataImport.templates',
            href: '/settings/data-import/templates',
            icon: LuFileSpreadsheet,
          },
          {
            id: 'field-mappings',
            label: 'Field Mappings',
            labelKey: 'navigation.settings.dataImport.fieldMappings',
            href: '/settings/data-import/field-mappings',
            icon: LuGitMerge,
          },
          {
            id: 'import-schemas',
            label: 'Import Schemas',
            labelKey: 'navigation.settings.dataImport.schemas',
            href: '/settings/data-import/schemas',
            icon: LuListTree,
          },
          {
            id: 'validation-rules',
            label: 'Validation Rules',
            labelKey: 'navigation.settings.dataImport.validationRules',
            href: '/settings/data-import/validation-rules',
            icon: LuShieldCheck,
          },
          {
            id: 'transformers',
            label: 'Transformers',
            labelKey: 'navigation.settings.dataImport.transformers',
            href: '/settings/data-import/transformers',
            icon: LuWand,
          },
          {
            id: 'enum-dictionaries',
            label: 'Enum Dictionaries',
            labelKey: 'navigation.settings.dataImport.enumDictionaries',
            href: '/settings/data-import/enum-dictionaries',
            icon: LuBook,
          },
          {
            id: 'import-settings',
            label: 'Import Settings',
            labelKey: 'navigation.settings.dataImport.importSettings',
            href: '/settings/data-import/settings',
            icon: LuSettings,
          },
        ],
      },
    ],
  },
];

// Helper function to find navigation item by path
export function findNavigationItemByPath(
  items: NavigationItem[],
  path: string
): NavigationItem | null {
  for (const item of items) {
    if (item.href === path) {
      return item;
    }
    if (item.children) {
      const found = findNavigationItemByPath(item.children, path);
      if (found) return found;
    }
  }
  return null;
}

// Helper function to get breadcrumb items from path
export function getBreadcrumbItems(path: string): NavigationItem[] {
  const segments = path.split('/').filter(Boolean);
  const breadcrumbs: NavigationItem[] = [];
  let currentPath = '';

  for (let i = 0; i < segments.length; i++) {
    currentPath += `/${segments[i]}`;
    const item = findNavigationItemByPath(mvpNavigationItems, currentPath);

    if (item) {
      breadcrumbs.push(item);
    } else {
      breadcrumbs.push({
        id: segments[i],
        label: segments[i].charAt(0).toUpperCase() + segments[i].slice(1),
        href: currentPath,
      });
    }
  }

  return breadcrumbs;
}

// Helper function to get localized navigation items
export function getLocalizedNavigationItems(
  items: NavigationItem[],
  t: (key: string) => string
): NavigationItem[] {
  return items.map((item) => {
    const localizedItem = { ...item };

    if (item.labelKey) {
      localizedItem.label = t(item.labelKey);
    }

    if (item.children) {
      localizedItem.children = getLocalizedNavigationItems(item.children, t);
    }

    return localizedItem;
  });
}
